'use strict';

/**
 * Cross-tab (and same-tab opt-in) publish/subscribe over BroadcastChannel,
 * with a `localStorage` `storage` event fallback when BC is unavailable.
 *
 * Wire envelope: `{ type, payload, nonce, timestamp }`.
 * - `type` — topic name
 * - `payload` — domain data for that topic
 * - `nonce` — this PubSub instance id (echo filter), not request correlation
 * - `timestamp` — sender wall clock (ms)
 *
 * Typical storefront use: one shared instance via `sharedStorage.tabChannel`
 * (`channelName` `'tab_bus'`).
 */
export class PubSub {
  /**
   * BroadcastChannel / logical channel name.
   *
   * @private
   * @type {string|null}
   */
  channelName = null;

  /**
   * `localStorage` key used when BroadcastChannel is unavailable.
   *
   * @private
   * @type {string|null}
   */
  storageKey = null;

  /**
   * Opaque id for this instance (self-filter on inbound envelopes).
   *
   * @private
   * @type {string|null}
   */
  nonce = null;

  /**
   * Topic → callback list.
   *
   * @private
   * @type {Record<string, Function[]>|null}
   */
  listeners = null;

  /**
   * Native channel when supported; otherwise `null` and storage fallback is used.
   *
   * @private
   * @type {BroadcastChannel|null}
   */
  channel = null;

  /**
   * Bound `storage` listener (fallback path only) so {@link PubSub#destroy} can remove it.
   *
   * @private
   * @type {function(StorageEvent): void|null}
   */
  onStorageEvent = null;

  /**
   * Open the channel (or storage fallback) for `channelName`.
   *
   * @param {string} [channelName='app_bus']
   */
  constructor(channelName = 'app_bus') {
    this.channelName = channelName;
    this.storageKey = 'honda_pubsub_' + channelName;
    this.nonce = Math.random().toString(36).slice(2);
    this.listeners = Object.create(null);

    if (typeof window.BroadcastChannel === 'function') {
      this.channel = new window.BroadcastChannel(channelName);
      this.channel.addEventListener('message', event => {
        this.handleMessage(event.data);
      });
    } else {
      this.onStorageEvent = event => this.handleStorage(event);
      window.addEventListener('storage', this.onStorageEvent);
    }
  }

  /**
   * Subscribe to a topic. Idempotent for the same callback reference.
   *
   * @param {string} type
   * @param {function(*, {
   *   type: string,
   *   payload: *,
   *   nonce: string,
   *   timestamp: number
   * }): void} callback `(payload, envelope)`
   *
   * @returns {function(): void} unsubscribe
   */
  subscribe(type, callback) {
    if (!this.listeners[type]) {
      this.listeners[type] = [];
    }

    if (this.listeners[type].indexOf(callback) === -1) {
      this.listeners[type].push(callback);
    }

    return () => {
      const list = this.listeners[type];

      if (!list) {
        return;
      }

      const index = list.indexOf(callback);

      if (index !== -1) {
        list.splice(index, 1);
      }

      if (list.length === 0) {
        delete this.listeners[type];
      }
    };
  }

  /**
   * Publish to peer browsing contexts.
   *
   * BroadcastChannel and the storage fallback never deliver back to the
   * publishing instance. Pass `notifySelf=true` when the same document must
   * also run local subscribers (tests, same-tab fan-out). Storefront topics
   * leave the default `false`.
   *
   * @param {string} type
   * @param {*} [payload]
   * @param {boolean} [notifySelf=false] also dispatch to this instance
   */
  publish(type, payload, notifySelf = false) {
    const envelope = {
      type: type,
      payload: payload,
      nonce: this.nonce,
      timestamp: Date.now()
    };

    if (this.channel) {
      try {
        this.channel.postMessage(envelope);
      } catch (e) {
        // Best-effort when the channel is closed or the payload is not cloneable.
      }
    } else {
      try {
        localStorage.setItem(this.storageKey, JSON.stringify(envelope));
        localStorage.removeItem(this.storageKey);
      } catch (e) {
        // Best-effort when storage is unavailable.
      }
    }

    if (notifySelf) {
      this.dispatch(envelope);
    }
  }

  /**
   * Tear down the transport and drop all subscribers.
   *
   * Part of the PubSub lifecycle API (pair with the constructor). The
   * storefront keeps a long-lived `sharedStorage.tabChannel` for the page
   * lifetime and does not call this; useful for tests or a short-lived bus.
   */
  destroy() {
    this.listeners = Object.create(null);

    if (this.channel) {
      this.channel.close();
      this.channel = null;
    }

    if (this.onStorageEvent) {
      window.removeEventListener('storage', this.onStorageEvent);
      this.onStorageEvent = null;
    }
  }

  /**
   * storage-event fallback: parse and route envelopes for this `storageKey`.
   *
   * @private
   *
   * @param {StorageEvent} event
   */
  handleStorage(event) {
    if (event.key !== this.storageKey || !event.newValue) {
      return;
    }

    try {
      this.handleMessage(JSON.parse(event.newValue));
    } catch (e) {
      // Ignore malformed messages from unrelated/old storefront code.
    }
  }

  /**
   * Ignore self envelopes (`nonce`); dispatch peers to topic subscribers.
   *
   * @private
   *
   * @param {{
   *   type: string,
   *   payload: *,
   *   nonce: string,
   *   timestamp: number
   * }} envelope
   */
  handleMessage(envelope) {
    if (!envelope || !envelope.type) {
      return;
    }

    if (envelope.nonce === this.nonce) {
      return;
    }

    this.dispatch(envelope);
  }

  /**
   * Invoke all subscribers for `envelope.type` with `(payload, envelope)`.
   *
   * @private
   *
   * @param {{
   *   type: string,
   *   payload: *,
   *   nonce: string,
   *   timestamp: number
   * }} envelope
   */
  dispatch(envelope) {
    const list = this.listeners[envelope.type] || [];

    list.forEach(callback => {
      callback(envelope.payload, envelope);
    });
  }
}
